import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { Grid, GridCol, GridRow } from '@naf/grid';
import { TextVariant } from '@naf/text';
import { useAppInsightsContext, useTrackMetric } from '@microsoft/applicationinsights-react-js';
import { AuthorBio } from '@naf-simba/sections-authorbio';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import useSelector from '@redux/typedHooks';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import { actions as articleActions, getArticleListKey } from '@redux/modules/articles';
import FeedbackSurvey from '@components/feedbacksurvey/FeedbackSurvey';
import BreadCrumb from '@components/breadcrumb/BreadCrumb';
import { LayoutWithMainContent } from '@components/layout/Layout';
import { SiteStructureType } from '@appTypes/siteStructureType';
import ArticleGrid from '@components/article-grid/ArticleGrid';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import InfinteScroll from '@components/infinite-scroll/InfinteScroll';
import { NotFound } from '@loadable-elements/NotFound';
import { useAuth0Token } from '@hooks/useAuth0Token';
import S from '@styles/articles/StyledArticle';

interface Props {
  category: SiteStructureType;
}

const getAuthorSlugFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  return params.get('forfatter') || params.get('forfattere') || undefined;
};

const AllArticlesList = ({ category }: Props) => {
  const appInsights = useAppInsightsContext();
  useTrackMetric(appInsights, 'Artikkel-liste');
  const dispatch = useDispatch();
  const { search } = useLocation() as { search: string };
  const authorSlug = getAuthorSlugFromSearch(search);
  const articleListRequest = useMemo(() => ({ category: null, authorSlug }), [authorSlug]);
  const articleListKey = getArticleListKey(articleListRequest);
  const { pages, isLoadingMore, total, totalFetchedArticles, errorState, editors } =
    useSelector((state) => state.articles.data[articleListKey]) || {};
  const { getProcessedImage } = useCloudinary();
  const { simpleToken } = useAuth0Token();
  // Seeded from the server prefetch, which is necessarily unauthenticated. The effect below is
  // gated on this ref, so without seeding it signed-in users would stay on the anonymous list.
  const initialRequestMadeWithoutToken = useRef(Boolean(pages && Object.values(pages).length > 0));
  const hasArticlesByAuthor = ((total ?? 0) > 0 || (totalFetchedArticles ?? 0) > 0) && !!editors?.length;
  const shouldShowAuthorIngress = !!authorSlug && hasArticlesByAuthor;

  // Memoised: InfinteScroll keys its IntersectionObserver effect on this callback, so a fresh
  // identity every render rebuilt the observer every render.
  const onShowMoreBtnClick = useCallback(() => {
    dispatch(articleActions.getMoreArticles.request(articleListRequest));
  }, [dispatch, articleListRequest]);

  useEffect(() => {
    if (!pages || Object.values(pages).length === 0) {
      dispatch(articleActions.getArticleList.request(articleListRequest));

      if (!simpleToken) {
        initialRequestMadeWithoutToken.current = true;
      }
    }
  }, [pages, dispatch, simpleToken, articleListRequest]);

  useEffect(() => {
    if (initialRequestMadeWithoutToken.current && simpleToken) {
      // Token is not always ready on first render, so we need to check if it is available.
      dispatch(articleActions.getArticleList.request(articleListRequest));
      initialRequestMadeWithoutToken.current = false;
    }
  }, [simpleToken, dispatch, articleListRequest]);

  const windowPath = useWindowLocation();

  if (!category) return <NotFound />;

  const pageTitle = category.seoConfig?.title || category.slug.replace('-', ' ');
  const headerVariant = TextVariant.Header1;
  const ingressVariant = TextVariant.Ingress;
  const ingressText = authorSlug ? editors?.[0]?.bio || category?.ingress : category?.ingress;

  if (category) {
    const categoryImage = getProcessedImage(category?.image?.publicId, {
      width: 1200,
      height: 630,
      quality: 'auto:eco',
    })?.toURL();

    return (
      <LayoutWithMainContent
        title={category.name || pageTitle}
        description={category?.ingress || category?.seoConfig?.introduction}
        url={windowPath}
        imgUrl={categoryImage || ''}
        gtmArgs={{
          page_type: 'AllArticlesListPage',
          pageCategory: category?.url || '',
          contentId: category?._id || '',
        }}
        seoTitle={pageTitle}
        seoDescription={category?.seoConfig?.introduction}
        internalSearchMetaData={{
          cardType: InternalSearchCardType.Simple,
          imagePublicId: category?.image?.publicId,
        }}
        backToTopButton
      >
        <S.Wrapper>
          <Grid>
            <GridCol s="12" m="12" l="12" xl="12">
              <BreadCrumb />
            </GridCol>
            <GridCol s="12" m="12" l="12" xl="12">
              <S.TitleAndAuthorBioWrapper>
                <S.TitleWrapper>
                  <S.CategoryTitle tag="h1" variant={headerVariant}>
                    {category?.name || pageTitle}
                  </S.CategoryTitle>
                  {category?.ingress && !shouldShowAuthorIngress && (
                    <S.Ingress tag="p" variant={ingressVariant}>
                      {category.ingress}
                    </S.Ingress>
                  )}
                </S.TitleWrapper>
                {!!editors?.length && (
                  <S.AuthorBioSection>
                    <S.AuthorBioList>
                      {editors.map((editor) => (
                        <S.AuthorBioWrapper key={editor.name}>
                          <AuthorBio
                            title={editor.name}
                            body={editor.position || ''}
                            image={editor.imageUrl ? { src: editor.imageUrl, alt: editor.name } : undefined}
                          />
                        </S.AuthorBioWrapper>
                      ))}
                    </S.AuthorBioList>
                  </S.AuthorBioSection>
                )}
              </S.TitleAndAuthorBioWrapper>
              {ingressText && shouldShowAuthorIngress && (
                <S.Ingress tag="p" variant={TextVariant.Ingress}>
                  {ingressText}
                </S.Ingress>
              )}
            </GridCol>
            <GridRow>
              {pages && (
                <InfinteScroll
                  showLoadingTrigger={!isLoadingMore && totalFetchedArticles < total}
                  onTrigger={onShowMoreBtnClick}
                >
                  {Object.entries(pages).map(([key, { data, isLoading }]) => (
                    <ArticleGrid
                      key={key}
                      articleItems={data}
                      updating={isLoading}
                      error={typeof errorState !== 'undefined'}
                      showDate
                      filterByAuthor={authorSlug !== undefined}
                      isLeadPage={key === '1'}
                    />
                  ))}
                </InfinteScroll>
              )}
            </GridRow>
            <GridCol s="12" m="12" l="12" xl="12">
              <p>
                Viser {totalFetchedArticles} av {total}
              </p>
            </GridCol>
            <GridRow>
              <GridCol s="12" m="12" l="8" xl="8">
                <FeedbackSurvey contentUrl={windowPath} />
              </GridCol>
            </GridRow>
          </Grid>
        </S.Wrapper>
      </LayoutWithMainContent>
    );
  }
  return null;
};

export default AllArticlesList;
